import React, { useState, useCallback, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { OrganizationBlockedOfficersService, OfficerService } from "@services";
import utils from "@utils";
import { OFFICER_STATUS_TO_FETCH_DATA } from "@constants";
import { Link } from "react-router";
import { Image, ScrollDetect, Switch } from "../../../components";
import { BlockedOfficersModal } from "./components";

import "./BlockedOfficers.scss";

const BlockedOfficers = ({ user, router }) => {
	const { lang } = global;
	const displayCount = 25;

	const _blockedOfficersModal = useRef(null);

	const [officers, setOfficers] = useState([]);
	const [hasMore, setHasMore] = useState(true);
	const [pageNumber, setPageNumber] = useState(1);
	const [visible, setVisible] = useState(false);
	const [showMyOfficers, setShowMyOfficers] = useState(user.isLEA());
	const [search, setSearch] = useState({
		activeSearch: false,
		searchQuery: "",
	});

	/** Gets the list of blocked officers */
	const getBlockedOfficers = useCallback(() => {
		OrganizationBlockedOfficersService.getAll({ pageNumber, displayCount })
			.then(({ data }) => {
				const newData = data?.ItemList ?? [];

				let newOfficers = newData.map(({ Officer, Note: note }) => ({
					id: Officer.UserId,
					image: Officer.ContactPhotoUrl,
					name: `${Officer.FirstName} ${Officer.LastName}`,
					rank: Officer.OfficerRank,
					jurisdictionId: Officer.JurisdictionId,
					jurisdiction: Officer.JurisdictionName,
					email: Officer.Email,
					blocked: true,
					note,
				}));

				if (showMyOfficers) {
					newOfficers = newOfficers.filter(
						item => item.jurisdictionId === user.Organization.JurisdictionId,
					);
				}

				setOfficers(pageNumber > 1 ? [...officers, ...newOfficers] : newOfficers);
				setPageNumber(pageNumber + 1);
				setHasMore(newOfficers.length >= displayCount);
				setSearch({ searchQuery: "", activeSearch: false });
			})
			.catch(error => {
				setOfficers([]);
				setPageNumber(1);
				setHasMore(false);
				utils.notify({
					type: "error",
					message: error.response.data.Message || lang.t("Error getting Officer Groups"),
				});
			});
	});

	/**
	 * Updates the selected blocked officers status
	 * @param {Number} officerId The unique id of the officer being selected
	 * @param {Boolean} isBlocking Determines which service call to use for updating the officers status
	 * @param {String} note The note or reason for blocking the officer
	 */
	const updateBlockedOfficerStatus = (officerId, isBlocking, note = "") => {
		const method = isBlocking ? "update" : "delete";
		OrganizationBlockedOfficersService[method](null, { officerId, note })
			.then(() => {
				if (isBlocking) _blockedOfficersModal.current.resetModal();
				utils.notify({
					type: "success",
					message: isBlocking
						? lang.t("Officer added to Do Not Hire list")
						: lang.t("Officer removed from Do Not Hire list"),
				});
				setSearch({ searchQuery: "", activeSearch: true });
			})
			.catch(() => {
				utils.notify({
					type: "error",
					message: lang.t("Error updating Blocked Status"),
				});
			});
	};

	/**
	 * Determines whether to open a modal or continue with updating the officers status
	 * @param {Number} officerId The unique id of the officer being selected
	 * @param {String} officerName The name of the selected officer
	 * @param {Boolean} isBlocking Determines which method to use based on the conditional
	 */
	const onBlockedStatusUpdate = (officerId, officerName, isBlocking) => () => {
		if (isBlocking) {
			_blockedOfficersModal.current.openModal({ officerId, officerName });
		} else {
			updateBlockedOfficerStatus(officerId, isBlocking);
		}
	};

	/** Searches for an officer from the blocked officers list */
	const searchOfficers = useCallback(() => {
		const searchData = {
			firstName: search.searchQuery,
			lastName: search.searchQuery,
			emailAddress: search.searchQuery,
			mobilePhone: search.searchQuery,
			zipCode: search.searchQuery,
			isAdvancedSearch: false,
		};
		// if the user is a LEA, it can search for all officers
		if (user.isLEA()) {
			searchData.includeNonJurisdiction = true;
		}

		OfficerService.search(
			{
				pageNumber,
				displayCount,
				vettedStatus: OFFICER_STATUS_TO_FETCH_DATA,
				includeNonJurisdiction: !showMyOfficers,
				includeDeleted: false,
				jobCount: true,
				totalJobsWorked: false,
				upcomingJobsScheduled: false,
			},
			searchData,
		)
			.then(({ data }) => {
				const newData = data?.ItemList ?? [];
				const newOfficers = newData.map(officer => ({
					id: officer.Id,
					image: officer.ContactPhotoUrl,
					name: `${officer.FirstName} ${officer.LastName}`,
					jurisdiction: officer.Department,
					experience: officer.JobCount,
					email: officer.EmailAddress,
					grouped: officer.IsFavorite,
					blocked: officer.DoNotHire,
				}));

				setOfficers(pageNumber > 1 ? [...officers, ...newOfficers] : newOfficers);
				setPageNumber(pageNumber + 1);
				setHasMore(newOfficers.length >= displayCount);
			})
			.catch(({ response }) => {
				utils.notify({
					type: "error",
					message: response.data.Message || lang.t("Error searching Officers"),
				});
			});
	});

	const changeSearch = ({ target: { value: searchQuery } }) => {
		setSearch({ searchQuery, activeSearch: false });
	};

	const handleSearch = () => {
		setSearch({ ...search, activeSearch: true });
	};

	const handleShowMyOfficersSwitchChange = () => {
		setPageNumber(1);
		setOfficers([]);
		setHasMore(true);
		setShowMyOfficers(prevState => !prevState);
	};

	const onKeyPress = ({ key }) => {
		// Execute search onPress Enter
		if (key === "Enter") handleSearch();
	};

	/** React to only my officers switch */
	useEffect(() => {
		if (search.activeSearch && search.searchQuery !== "") searchOfficers();
		else getBlockedOfficers();
	}, [showMyOfficers]);

	/** Reset visibility of the scrolldetect element after it has become visible once */
	useEffect(() => {
		if (visible) {
			if (search.activeSearch && search.searchQuery !== "") searchOfficers();
			else getBlockedOfficers();
			setVisible(false);
		}
	}, [visible]);

	useEffect(() => {
		if (search.activeSearch) {
			setPageNumber(1);
			setOfficers([]);
			setHasMore(true);
		}
	}, [search]);

	/** Access to the Do Not Hire list should be kept to admin staff only */
	useEffect(() => {
		if (!user.IsStaffAdmin) {
			router.push("/groups/list/officers");
		}
	}, []);

	return (
		<div
			id="blocked-officers"
			className="container"
		>
			<div className="row page-title text-center">
				<div className="col-sm-9">
					<h1>{lang.t("Do Not Hire")}</h1>
				</div>
				<div className="col-sm-3 col-md-3">
					<div className="my-officers-switch">
						<If condition={user.isLEA()}>
							<Switch
								label={lang.t("My Officers Only")}
								onClick={handleShowMyOfficersSwitchChange}
								on={showMyOfficers}
							/>
						</If>
					</div>
				</div>
			</div>
			<div className="row officer-search">
				<div className="col-md-2">
					<h4>{lang.t("Search Officers")}</h4>
				</div>
				<div className="col-md-6">
					<div className="input-group">
						<input
							className="form-control"
							type="text"
							value={search.searchQuery}
							onChange={changeSearch}
							onKeyPress={onKeyPress}
						/>
						<span className="input-group-btn">
							<button
								type="button"
								className="btn btn-default"
								onClick={() => handleSearch()}
							>
								<i className="rkf rkf-search" />
							</button>
						</span>
					</div>
				</div>
			</div>
			<div className="row header-row hidden-sm hidden-xs">
				<div className="col-md-1" />
				<div className="col-md-2">
					<strong>{lang.t("Name")}</strong>
				</div>
				<div className="col-md-2">
					<strong>{lang.t("Jurisdiction")}</strong>
				</div>
				<div className="col-md-3">
					<strong>{lang.t("EMail")}</strong>
				</div>
				<div className="col-md-2">
					<strong>{search.activeSearch ? lang.t("Experience") : lang.t("Note")}</strong>
				</div>
				<div className="col-md-2" />
			</div>
			<div className="container-fluid table-body">
				<For
					each="officer"
					index="index"
					of={officers}
				>
					<div
						key={`${index}_${officer.id}`}
						className="row v-align-md"
					>
						<div className="col-md-1 hiddem-sm hidden-xs">
							<Link to={`/officers/${officer.id}`}>
								<Image
									src={officer.image}
									fallbackSrc="avatar"
									className="hidden-sm"
								/>
							</Link>
						</div>
						<div className="col-md-2 col-xs-9">
							<Link to={`/officers/${officer.id}`}>{officer.name}</Link>
							<div className="visible-md">
								<strong>#{officer.id}</strong>
							</div>
							<div>{officer.rank}</div>
						</div>
						<div className="col-xs-3 visible-sm-block visible-xs-block">
							<strong>#{officer.id}</strong>
						</div>
						<div className="col-xs-12 col-md-2">{officer.jurisdiction}</div>
						<div className="col-xs-12 col-md-3 truncate">
							<a
								href={`mailto:${officer.email}`}
								title={officer.email}
							>
								{officer.email}
							</a>
						</div>
						<div className="col-xs-12 col-md-2">
							<Choose>
								<When condition={officer.experience}>
									{`${officer.experience || 0} job${officer.experience !== 1 ? "s" : ""} worked`}
								</When>
								<Otherwise>{officer.note}</Otherwise>
							</Choose>
						</div>
						<div className="col-xs-12 col-sm-6 col-sm-offset-3 col-md-2 col-md-offset-0">
							<button
								type="button"
								className={`btn btn-${officer.blocked ? "danger" : "primary"} btn-block`}
								onClick={onBlockedStatusUpdate(
									officer.id,
									officer.name,
									!officer.blocked,
								)}
							>
								{officer.blocked ? lang.t("Remove") : lang.t("Add")}
							</button>
						</div>
					</div>
				</For>
				<If condition={hasMore}>
					<ScrollDetect
						className="text-center"
						onVisible={() => setVisible(true)}
					>
						<i className="glyphicon glyphicon-repeat rkf-spin rkf-fw" />
					</ScrollDetect>
				</If>
			</div>
			<BlockedOfficersModal
				ref={_blockedOfficersModal}
				updateBlockedOfficerStatus={updateBlockedOfficerStatus}
			/>
		</div>
	);
};

BlockedOfficers.propTypes = {
	user: PropTypes.object,
	router: PropTypes.object,
};

BlockedOfficers.defaultProps = {
	user: {},
	router: {},
};

export default BlockedOfficers;
